import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';
import Sidebar from '../../../../component/includes/sidebar';
import { CreateProductCategoryData, ProductCategory, productCategoryService, UpdateProductCategoryData } from '../../../../services/product/product-category.service';

const ProductCategoryManagementPage: React.FC = () => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [newCategory, setNewCategory] = useState<CreateProductCategoryData>({ name: '' });
  const [editingCategory, setEditingCategory] = useState<UpdateProductCategoryData | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const categoriesData = await productCategoryService.getProductCategories();

      let categoryArray: ProductCategory[] = [];

      if (Array.isArray(categoriesData)) {
        categoryArray = categoriesData;
      } else if (categoriesData && typeof categoriesData === 'object') {
        categoryArray =
          (categoriesData as any)?.data ??
          (categoriesData as any)?.categories ??
          Object.values(categoriesData).find((val: any) => Array.isArray(val)) ??
          [];
      }

      setCategories(Array.isArray(categoryArray) ? categoryArray : []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      setError('Category name cannot be empty');
      return;
    }

    try {
      await productCategoryService.createProductCategory(newCategory);
      setNewCategory({ name: '' });
      setSuccessMessage('Category created successfully');
      await fetchCategories();
      setIsModalOpen(false);
    } catch (error: any) {
      setError(error?.error || error?.message || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      await productCategoryService.updateProductCategory(editingCategory);
      setEditingCategory(null);
      setSuccessMessage('Category updated successfully');
      await fetchCategories();
      setIsModalOpen(false);
    } catch (error: any) {
      setError(error?.error || error?.message || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (id: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this category?');
    if (!confirmed) return;

    try {
      await productCategoryService.deleteProductCategory(id);
      setSuccessMessage('Category deleted successfully');
      await fetchCategories();
    } catch (error: any) {
      setError(error?.error || error?.message || 'Failed to delete category');
    }
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar/>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Navbar */}
{/* Page Content */}
        <div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            
            {/* Header - Mirip Gambar */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">📦</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Product Categories</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage your product categories efficiently</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingCategory(null);
                  setNewCategory({ name: '' });
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all"
              >
                <Plus className="w-5 h-5" />
                Add Category
              </button>
            </div>

            {/* Search Bar - Mirip Gambar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search categories by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table - Mirip Gambar */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">CATEGORY NAME</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-36">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCategories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                      {/* ID Circle */}
                      <td className="px-8 py-5">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {category.id}
                        </div>
                      </td>

                      {/* Category Name with Green Bullet */}
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          <span className="text-green-500 text-2xl">●</span>
                          <span className="text-gray-800 font-medium text-lg">
                            {category.name}
                          </span>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-5">
                        <div className="flex items-center justify-end gap-4">
                          <button
                            onClick={() => {
                              setEditingCategory({ id: category.id, name: category.name });
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredCategories.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-16 text-center text-gray-400">
                        No categories found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-semibold mb-8">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h2>
            <input
              type="text"
              value={editingCategory ? editingCategory.name : newCategory.name}
              onChange={(e) =>
                editingCategory
                  ? setEditingCategory({ ...editingCategory, name: e.target.value })
                  : setNewCategory({ name: e.target.value })
              }
              placeholder="Enter category name..."
              className="w-full border border-gray-300 rounded-2xl px-6 py-4 text-lg focus:outline-none focus:border-purple-500"
              autoFocus
            />
            <div className="flex gap-4 mt-10">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium"
              >
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {successMessage && (
        <div className="fixed bottom-6 right-6 bg-green-500 text-white px-6 py-4 rounded-2xl shadow-xl z-50">
          ✅ {successMessage}
        </div>
      )}
      {error && (
        <div className="fixed bottom-6 right-6 bg-red-500 text-white px-6 py-4 rounded-2xl shadow-xl z-50">
          {error}
        </div>
      )}
    </div>
  );
};

export default ProductCategoryManagementPage;